import React, { useEffect, useState, useCallback } from 'react';
import { History, ShieldAlert, ShieldCheck, ShieldX, Tag, RefreshCw, Loader2, Clock } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const STATUS_META = {
  pending:  { label: 'Pending',  cls: 'bg-amber-100 text-amber-800',    Icon: Clock },
  granted:  { label: 'Active',   cls: 'bg-emerald-100 text-emerald-800',Icon: ShieldCheck },
  denied:   { label: 'Denied',   cls: 'bg-slate-100 text-slate-700',    Icon: ShieldX },
  revoked:  { label: 'Revoked',  cls: 'bg-red-100 text-red-800',        Icon: ShieldX },
  expired:  { label: 'Expired',  cls: 'bg-slate-100 text-slate-600',    Icon: Clock },
};

function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

function derivedStatus(s) {
  if (s.status === 'granted' && s.expires_at && new Date(s.expires_at).getTime() < Date.now()) {
    return 'expired';
  }
  return s.status;
}

export default function SupportHistoryPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall('/api/employer/support-mode/history?limit=50');
      setRows(r.history || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load access history');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="support-history-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h4 className="font-semibold text-slate-900 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-600" /> Support access history
        </h4>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
          data-testid="support-history-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      <p className="text-xs text-slate-500 px-4 pt-3">
        Every past request from a platform administrator — who asked, when, what areas they were granted,
        and whether the session ended by revoke or expiry.
      </p>
      {loading && rows.length === 0 ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-slate-500" data-testid="support-history-empty">
          No past support sessions.
        </p>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          <ul className="divide-y divide-slate-100">
            {rows.map((s, i) => {
              const st = derivedStatus(s);
              const meta = STATUS_META[st] || STATUS_META.expired;
              const Icon = meta.Icon;
              const scopes = s.scopes || ['all'];
              const scopesLabel = scopes.includes('all') ? 'Full access' : scopes.join(' · ');
              return (
                <li
                  key={s._id}
                  className="px-4 py-3"
                  data-testid={`support-history-row-${i}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {s.admin_name || s.admin_email}
                      </p>
                      <p className="text-xs text-slate-500 truncate">
                        {s.admin_email}
                        <span className="mx-1.5 text-slate-300">·</span>
                        {s.mode === 'forced' ? 'Forced entry' :
                         s.mode === 'consent' ? 'Consent' :
                         s.mode === 'legacy' ? 'Legacy' : (s.mode || 'session')}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  {s.reason && (
                    <p className="mt-1.5 text-xs text-slate-600 whitespace-pre-line">
                      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Reason: </span>
                      {s.reason}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <Tag className="w-3 h-3" /> {scopesLabel}
                    </span>
                    <span>Requested {fmtWhen(s.requested_at)}</span>
                    {s.decided_at && <span>Decided {fmtWhen(s.decided_at)}</span>}
                    {s.expires_at && <span>Expired {fmtWhen(s.expires_at)}</span>}
                  </div>
                  {s.decision_note && (
                    <p className="mt-1 text-[11px] text-slate-500 italic">
                      Note back to admin: "{s.decision_note}"
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
